// Copyright ©️ 2026 eVolve MEP, LLC

import { Anchor, Box, Group, Stack, Text, TextInput } from '@mantine/core';

import { StepperInput } from 'components/Mantine/StepperInput';
import { isNotNil } from 'helpers/isNotNil';
import { BendValidationStatus } from 'modules/Shop/EvolveFabrication/BendCreator/FormComponents/BendValidationStatus';
import { useBendCreatorFormContext } from 'modules/Shop/EvolveFabrication/BendCreator/FormComponents/useBendCreatorFormContext';
import {
  bendCreatorStepIndexes,
  bendCreatorSteps,
  REVIEW_STEP,
} from 'modules/Shop/EvolveFabrication/BendCreator/Nav/bendCreatorSteps';
import classes from 'modules/Shop/EvolveFabrication/BendCreator/Nav/steps/steps.module.css';
import {
  formatBendDimension,
  formatMaterial,
  getEnteredDimensions,
} from 'modules/Shop/EvolveFabrication/BendCreator/Nav/stepValues';
import { useStepStatuses } from 'modules/Shop/EvolveFabrication/BendCreator/Nav/useStepStatuses';
import { useBendBuilder } from 'modules/Shop/EvolveFabrication/BendCreator/State/useBendBuilder';
import { useBendTypes } from 'modules/Shop/EvolveFabrication/BendCreator/useBendTypes';
import { useConduits } from 'modules/Shop/EvolveFabrication/useConduits';

type SpecRowProps = {
  label: string;
  value: string;
};

const SpecRow = ({ label, value }: SpecRowProps) => (
  <Box className={classes.specRow}>
    <Text component="span" className={classes.specLabel}>
      {label}
    </Text>
    <Text component="span" className={classes.specValue}>
      {value}
    </Text>
  </Box>
);

export const ReviewStep = () => {
  const form = useBendCreatorFormContext();
  const { setIsAutoNamed, openStep } = useBendBuilder();
  const statuses = useStepStatuses();
  const { bendTypes } = useBendTypes();
  const { conduitTypes, conduitSizes } = useConduits();

  const bendType = bendTypes?.find(({ name }) => name === form.values.bendType) ?? null;
  const material = formatMaterial(form.values, { conduitTypes, conduitSizes });
  const missingSteps = bendCreatorStepIndexes.filter((step) => step !== REVIEW_STEP && !statuses[step].complete);
  const { onChange: onPartNameChange, ...partNameProps } = form.getInputProps('partName');

  return (
    <Stack gap={20}>
      <TextInput
        label="Name"
        required
        classNames={{ input: classes.textInput }}
        placeholder="Untitled bend"
        {...partNameProps}
        onChange={(event) => {
          // oxlint-disable-next-line typescript/no-unsafe-call
          onPartNameChange(event);
          setIsAutoNamed(false);
        }}
      />
      <Stack gap={4}>
        <StepperInput
          mode="count"
          label="Quantity"
          required
          min={1}
          max={99}
          step={1}
          suffix=" ea"
          {...form.getInputProps('quantity')}
        />
        <Text c="dimmed" fz="sm">
          Part name will be incremented when adding quantities over 1.
        </Text>
      </Stack>
      {missingSteps.length > 0 && (
        <Group gap={6}>
          <Text fz="sm">Still needed:</Text>
          {missingSteps.map((step) => (
            <Anchor key={step} component="button" type="button" fz="sm" onClick={() => openStep(step)}>
              {bendCreatorSteps[step].name}
            </Anchor>
          ))}
        </Group>
      )}
      <Stack gap={8}>
        <Text className={classes.sectionLabel}>Everything Decided</Text>
        <Box className={classes.spec}>
          {isNotNil(material) && <SpecRow label="Material" value={material} />}
          {isNotNil(form.values.bendType) && <SpecRow label="Bend Type" value={form.values.bendType} />}
          {getEnteredDimensions(form.values, bendType).map(({ input, value }) => (
            <SpecRow key={input.keyName} label={input.name} value={formatBendDimension(input, value)} />
          ))}
          {isNotNil(form.values.benderProfile) && <SpecRow label="Bender" value={form.values.benderProfile.name} />}
        </Box>
      </Stack>
      <BendValidationStatus currentBendTypeData={bendType} />
    </Stack>
  );
};
